import { useState } from "react";
import { Link } from "react-router-dom";
import { useUndervaluedGems } from "@/hooks/useUndervaluedGems";
import { UndervaluedGemCard } from "@/components/UndervaluedGemCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Gem, RefreshCw, Clock, ArrowLeft, BookOpen } from "lucide-react";

const UndervaluedGemsPage = () => {
  const { data, isLoading, refetch } = useUndervaluedGems(8);
  const { isAdmin } = useAuth() as any;
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: res, error } = await supabase.functions.invoke("refresh-undervalued-gems");
      if (error) throw error;
      if (!res?.success) throw new Error(res?.error || "Refresh failed");
      toast.success(`Refreshed ${res.count} gems`);
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const lastUpdated = data?.[0]?.last_updated;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-2">
              <Gem className="w-5 h-5 text-emerald-400" />
              <span className="type-eyebrow text-emerald-400">
                Fundamental Value Investing
              </span>
            </div>
            <h1 className="type-page-title text-4xl md:text-5xl bg-gradient-to-r from-emerald-300 via-cyan-300 to-primary bg-clip-text text-transparent">
              Top 8 Undervalued Gems on Swyftx
            </h1>
            <p className="text-muted-foreground mt-3 text-lg">
              Tokens our AI considers underpriced relative to real usage, revenue, and on-chain
              adoption — refreshed daily. Not financial advice.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {lastUpdated && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                Last updated {new Date(lastUpdated).toLocaleString("en-AU")}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/undervalued-gems/methodology">
                  <BookOpen className="w-4 h-4 mr-1" /> Methodology
                </Link>
              </Button>
              {isAdmin && (
                <Button onClick={handleRefresh} disabled={refreshing} size="sm">
                  <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
                  {refreshing ? "Refreshing…" : "Refresh Gems Now"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[440px] rounded-lg" />)}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-16 text-center text-muted-foreground">
            No gems yet. {isAdmin ? 'Click "Refresh Gems Now" to generate the first set.' : "Check back soon."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {data.map((g) => <UndervaluedGemCard key={g.id} gem={g} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default UndervaluedGemsPage;
